/**
 * ReferenceDataManager — PMA-REF wave settings screen.
 *
 * Route: /admin/reference-data (admin-only via ProtectedRoute in App.tsx)
 *
 * CRUD over the 5 admin-configurable reference-data categories:
 * player_position, season, competition_stage, match_type, venue_surface.
 * Soft-delete only (is_active=false) — deactivating is blocked server-side
 * (400) when the value is `is_locked` or is the last active value in its
 * category; this screen disables the control preemptively for the same two
 * cases but always defers to the server's answer.
 *
 * Value labels shown here ARE the source of truth (`labels.en`/`labels.es`)
 * — this screen is where an admin edits them. Screen chrome (buttons,
 * titles, validation copy) comes from `admin.json`'s `referenceData.*` keys.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDown,
  ArrowUp,
  Edit,
  Lock,
  Plus,
  RotateCcw,
  Trash2,
  X,
} from "../components/icons";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  useCreateReferenceValue,
  useReferenceValues,
  useReorderReferenceValues,
  useUpdateReferenceValue,
} from "../hooks/useReferenceValues";
import type {
  PositionGroup,
  ReferenceCategory,
  ReferenceValue,
} from "../lib/referenceDataApi";
import { REFERENCE_CATEGORIES } from "../lib/referenceDataApi";
import CompetitionsManager from "./CompetitionsManager";

/**
 * Competitions are NOT a `reference_values` category (client-confirmed
 * 2026-08-06, PMA-UI wave): they are real entities (`competition_id`,
 * gender, country, i18n names) that matches join to by id. Forking them
 * into code/label pairs here would create a second source of truth and
 * risk orphaning matches. This tab instead reuses `CompetitionsManager`
 * unchanged — same component, same route logic as `/admin/competitions`,
 * just mounted as a tab on this screen — so there is exactly one
 * Competitions CRUD implementation.
 */
type SettingsSection = ReferenceCategory | "competitions";

const POSITION_GROUPS: PositionGroup[] = [
  "goalkeeper",
  "defense",
  "midfield",
  "attack",
  "other",
];

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  const data = (err as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail;
  if (typeof data === "string") return data;
  return "An unexpected error occurred";
}

interface CreateFormState {
  code: string;
  labelEn: string;
  labelEs: string;
  group: PositionGroup;
  sortOrder: string;
}

const EMPTY_CREATE_FORM: CreateFormState = {
  code: "",
  labelEn: "",
  labelEs: "",
  group: "other",
  sortOrder: "0",
};

interface EditFormState {
  labelEn: string;
  labelEs: string;
}

export default function ReferenceDataManager() {
  const { t } = useTranslation("admin");

  const [category, setCategory] =
    useState<ReferenceCategory>("player_position");
  const [section, setSection] = useState<SettingsSection>("player_position");
  const { data, isLoading, error } = useReferenceValues(category, {
    includeInactive: true,
  });
  const values = data ?? [];
  const activeCount = values.filter((v) => v.is_active).length;

  const createMutation = useCreateReferenceValue();
  const updateMutation = useUpdateReferenceValue();
  const reorderMutation = useReorderReferenceValues();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] =
    useState<CreateFormState>(EMPTY_CREATE_FORM);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({
    labelEn: "",
    labelEs: "",
  });
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const handleSelectSection = (next: SettingsSection) => {
    setSection(next);
    if (next !== "competitions") {
      setCategory(next);
    }
    setShowCreateForm(false);
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateError(null);
    setEditingId(null);
  };

  const handleCreate = async () => {
    setCreateError(null);
    const code = createForm.code.trim();
    const labelEn = createForm.labelEn.trim();
    const labelEs = createForm.labelEs.trim();
    if (!code || !labelEn || !labelEs) {
      setCreateError(t("referenceData.validationRequired"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        category,
        code,
        labels: { en: labelEn, es: labelEs },
        group: category === "player_position" ? createForm.group : undefined,
        sort_order: Number(createForm.sortOrder) || 0,
      });
      setShowCreateForm(false);
      setCreateForm(EMPTY_CREATE_FORM);
    } catch (err) {
      setCreateError(getErrorMessage(err));
    }
  };

  const startEdit = (value: ReferenceValue) => {
    setEditingId(value._id);
    setEditForm({ labelEn: value.labels.en, labelEs: value.labels.es });
    setRowError((prev) => ({ ...prev, [value._id]: "" }));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = async (value: ReferenceValue) => {
    const labelEn = editForm.labelEn.trim();
    const labelEs = editForm.labelEs.trim();
    if (!labelEn || !labelEs) {
      setRowError((prev) => ({
        ...prev,
        [value._id]: t("referenceData.validationRequired"),
      }));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: value._id,
        payload: { labels: { en: labelEn, es: labelEs } },
      });
      setEditingId(null);
    } catch (err) {
      setRowError((prev) => ({ ...prev, [value._id]: getErrorMessage(err) }));
    }
  };

  const toggleActive = async (value: ReferenceValue) => {
    setRowError((prev) => ({ ...prev, [value._id]: "" }));
    try {
      await updateMutation.mutateAsync({
        id: value._id,
        payload: { is_active: !value.is_active },
      });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [value._id]: getErrorMessage(err) }));
    }
  };

  // The reorder endpoint requires `orderedIds` to be the category's full,
  // current id set (referenceDataApi.ts) — it 400s if this client's `values`
  // snapshot has gone stale relative to the server (e.g. another admin, or
  // another browser context in this same category, created/removed a value
  // in between). Unlike handleCreate/saveEdit/toggleActive above, this
  // handler used to await mutateAsync with no try/catch: a 400 here became
  // an unhandled promise rejection (a real defect, independent of test
  // timing — a genuine concurrent-admin collision in production would hit
  // the exact same silent crash, with no user-facing feedback). Caught and
  // surfaced the same way the sibling handlers already report row errors.
  const move = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= values.length) return;
    const reordered = [...values];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);
    setRowError((prev) => ({ ...prev, [moved._id]: "" }));
    try {
      await reorderMutation.mutateAsync({
        category,
        orderedIds: reordered.map((v) => v._id),
      });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [moved._id]: getErrorMessage(err) }));
    }
  };

  return (
    <div
      className="container mx-auto px-4 py-8 max-w-5xl"
      data-testid="reference-data-view"
    >
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-gray-900"
          data-testid="reference-data-heading"
        >
          {t("referenceData.title")}
        </h1>
        <p className="text-gray-600 mt-2">{t("referenceData.subtitle")}</p>
      </div>

      {/* Category tabs */}
      <div
        className="flex flex-wrap gap-2 mb-6"
        data-testid="reference-data-tabs"
      >
        {REFERENCE_CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => handleSelectSection(cat)}
            data-testid={`refdata-category-tab-${cat}`}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              section === cat
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            {t(`referenceData.categories.${cat}`)}
          </button>
        ))}
        {/* Competitions: not a reference-data category — see the
            `SettingsSection` comment above the type definition. */}
        <button
          type="button"
          onClick={() => handleSelectSection("competitions")}
          data-testid="refdata-category-tab-competitions"
          className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            section === "competitions"
              ? "bg-primary-600 text-white"
              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
          }`}
        >
          {t("competitions")}
        </button>
      </div>

      {section === "competitions" ? (
        <div data-testid="reference-data-competitions-panel">
          <CompetitionsManager />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {t("referenceData.categoryDescription", {
                category: t(`referenceData.categories.${category}`),
              })}
            </p>
            <button
              type="button"
              onClick={() => {
                setShowCreateForm((prev) => !prev);
                setCreateForm(EMPTY_CREATE_FORM);
                setCreateError(null);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              data-testid="refdata-add-btn"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("referenceData.addValue")}
            </button>
          </div>

          {showCreateForm && (
            <div
              className="mb-6 rounded-xl border border-gray-200 bg-white p-6"
              data-testid="refdata-create-form"
            >
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t("referenceData.addValue")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("referenceData.code")}
                  </label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        code: e.target.value,
                      }))
                    }
                    className="input w-full"
                    data-testid="refdata-create-code"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("referenceData.sortOrder")}
                  </label>
                  <input
                    type="number"
                    value={createForm.sortOrder}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        sortOrder: e.target.value,
                      }))
                    }
                    className="input w-full"
                    data-testid="refdata-create-sort-order"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("referenceData.labelEn")}
                  </label>
                  <input
                    type="text"
                    value={createForm.labelEn}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        labelEn: e.target.value,
                      }))
                    }
                    className="input w-full"
                    data-testid="refdata-create-label-en"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t("referenceData.labelEs")}
                  </label>
                  <input
                    type="text"
                    value={createForm.labelEs}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        labelEs: e.target.value,
                      }))
                    }
                    className="input w-full"
                    data-testid="refdata-create-label-es"
                  />
                </div>
                {category === "player_position" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t("referenceData.group")}
                    </label>
                    <Select
                      value={createForm.group}
                      onValueChange={(next) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          group: next as PositionGroup,
                        }))
                      }
                    >
                      <SelectTrigger
                        className="input w-full"
                        data-testid="refdata-create-group"
                      >
                        <SelectValue>
                          {t(
                            `referenceData.positionGroups.${createForm.group}`,
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {POSITION_GROUPS.map((g) => (
                          <SelectItem key={g} value={g}>
                            {t(`referenceData.positionGroups.${g}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {createError && (
                <div
                  role="alert"
                  className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                  data-testid="refdata-create-error"
                >
                  {createError}
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createMutation.isPending}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  data-testid="refdata-create-save"
                >
                  {createMutation.isPending
                    ? t("referenceData.saving")
                    : t("referenceData.save")}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  {t("referenceData.cancel")}
                </button>
              </div>
            </div>
          )}

          {isLoading && (
            <div
              className="flex items-center justify-center py-12"
              data-testid="reference-data-loading"
            >
              <LoadingSpinner size="md" />
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              data-testid="reference-data-error"
            >
              {t("errorFetchingData")}: {getErrorMessage(error)}
            </div>
          )}

          {!isLoading && !error && (
            <div
              className="rounded-xl border border-gray-200 bg-white overflow-hidden"
              data-testid="reference-data-list"
            >
              {values.length === 0 ? (
                <p className="px-6 py-8 text-sm text-gray-500 text-center">
                  {t("referenceData.empty")}
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("referenceData.code")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("referenceData.labelEn")}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("referenceData.labelEs")}
                      </th>
                      {category === "player_position" && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          {t("referenceData.group")}
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {t("referenceData.status")}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t("actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {values.map((value, index) => {
                      const isEditing = editingId === value._id;
                      const isLastActive = value.is_active && activeCount <= 1;
                      const deactivateDisabled =
                        value.is_locked || isLastActive;
                      return (
                        <tr
                          key={value._id}
                          data-testid={`refdata-row-${value.code}`}
                          className={!value.is_active ? "bg-gray-50" : ""}
                        >
                          <td className="px-4 py-3 text-sm font-mono text-gray-700">
                            <div className="flex items-center gap-2">
                              {value.code}
                              {value.is_locked && (
                                <Lock
                                  className="h-3.5 w-3.5 text-gray-400"
                                  aria-label={t("referenceData.locked")}
                                  data-testid={`refdata-locked-${value.code}`}
                                />
                              )}
                            </div>
                          </td>
                          {isEditing ? (
                            <>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={editForm.labelEn}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      labelEn: e.target.value,
                                    }))
                                  }
                                  className="input w-full"
                                  data-testid={`refdata-edit-label-en-${value.code}`}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={editForm.labelEs}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      labelEs: e.target.value,
                                    }))
                                  }
                                  className="input w-full"
                                  data-testid={`refdata-edit-label-es-${value.code}`}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {value.labels.en}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {value.labels.es}
                              </td>
                            </>
                          )}
                          {category === "player_position" && (
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {value.group
                                ? t(
                                    `referenceData.positionGroups.${value.group}`,
                                  )
                                : "-"}
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                value.is_active
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                              data-testid={`refdata-status-${value.code}`}
                            >
                              {value.is_active
                                ? t("active")
                                : t("referenceData.deprecated")}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => saveEdit(value)}
                                    className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                                    data-testid={`refdata-edit-save-${value.code}`}
                                  >
                                    {t("referenceData.save")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={cancelEdit}
                                    className="text-gray-500 hover:text-gray-700"
                                    aria-label={t("referenceData.cancel")}
                                  >
                                    <X className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => move(index, -1)}
                                    disabled={
                                      index === 0 || reorderMutation.isPending
                                    }
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                    aria-label={t("referenceData.moveUp")}
                                    data-testid={`refdata-move-up-${value.code}`}
                                  >
                                    <ArrowUp
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => move(index, 1)}
                                    disabled={
                                      index === values.length - 1 ||
                                      reorderMutation.isPending
                                    }
                                    className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                    aria-label={t("referenceData.moveDown")}
                                    data-testid={`refdata-move-down-${value.code}`}
                                  >
                                    <ArrowDown
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(value)}
                                    className="text-gray-500 hover:text-gray-700"
                                    aria-label={t("referenceData.edit")}
                                    data-testid={`refdata-edit-btn-${value.code}`}
                                  >
                                    <Edit
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  {value.is_active ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleActive(value)}
                                      disabled={deactivateDisabled}
                                      title={
                                        deactivateDisabled
                                          ? value.is_locked
                                            ? t("referenceData.lockedHint")
                                            : t("referenceData.lastActiveHint")
                                          : undefined
                                      }
                                      className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                                      aria-label={t("referenceData.deactivate")}
                                      data-testid={`refdata-deactivate-btn-${value.code}`}
                                    >
                                      <Trash2
                                        className="h-4 w-4"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => toggleActive(value)}
                                      className="text-green-600 hover:text-green-800"
                                      aria-label={t("referenceData.reactivate")}
                                      data-testid={`refdata-reactivate-btn-${value.code}`}
                                    >
                                      <RotateCcw
                                        className="h-4 w-4"
                                        aria-hidden="true"
                                      />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            {rowError[value._id] && (
                              <p
                                className="mt-1 text-xs text-red-600"
                                data-testid={`refdata-row-error-${value.code}`}
                              >
                                {rowError[value._id]}
                              </p>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
